import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityLogDto,
  CreateDemandViewDto,
  CreateDemandViewResponseDto,
  DemandViewListEntryDto,
  GetAllDemandViewsDto,
  GetDemandViewByIdResponseDto,
  GetDemandViewsResponseDto,
  UpdateDemandViewDto,
  UpdateDemandViewResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandsQueryKeys } from '@/lib/query-keys/demands';
import { demandViewsQueryKeys } from '@/lib/query-keys/demandViews';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults, type WithUserData } from './useFim3Api';

export const useDemandViewsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demand-views`;
  const defaultLimit = 25;

  const getDemandViews = async (
    params: GetAllDemandViewsDto,
  ): Promise<
    Omit<GetDemandViewsResponseDto, 'data'> & { data: WithUserData<DemandViewListEntryDto>[] }
  > => {
    const { data } = (await httpClient.get(`${baseUrl}`, { params })) as {
      data: GetDemandViewsResponseDto;
    };

    const userIds = data.data.map((el) => el.ownerId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item) => item.ownerId,
    );

    return { ...data, data: result };
  };

  const getDemandView = async (id: number): Promise<GetDemandViewByIdResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const createDemandView = async (
    view: CreateDemandViewDto,
  ): Promise<CreateDemandViewResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, view);
    return data;
  };

  const updateDemandView = async (
    updateDemand: UpdateDemandViewDto & { id: number },
  ): Promise<UpdateDemandViewResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${updateDemand.id}`, {
      ...updateDemand,
      id: undefined,
    });
    return { data };
  };

  const archiveDemandView = async (id: number) => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/archive`);
    return { data };
  };

  const restoreDemandView = async (id: number) => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/restore`);
    return { data };
  };

  const deleteDemandView = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return { data };
  };

  const getDemandViewActivityLogs = async (id: number) => {
    const { data } = await httpClient.get<ActivityLogDto[]>(`${baseUrl}/${id}/activity-logs`);

    const userIds = data.map((el: ActivityLogDto) => el.userId);

    const result = await getUserDataForResults(
      httpClient,
      userIds,
      data as Array<{
        id: number;
        displayName?: string;
        creatorName?: string;
        email?: string;
      }>,
      (item) => (item as ActivityLogDto).userId,
    );

    return result as Array<ActivityLogDto & { displayName?: string; creatorName?: string }>;
  };

  const useGetAll = (params: GetAllDemandViewsDto = {}) => {
    const limit = params.limit ?? defaultLimit;
    const normalizedParams = { ...params, limit };
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      queryKey: demandViewsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) => getDemandViews({ ...params, limit, offset: pageParam * limit }),
      getNextPageParam: (lastPage) =>
        lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined,
    });
  };

  const useGetDemandView = (id: number) =>
    useQuery({
      queryKey: demandViewsQueryKeys.detail(id),
      queryFn: () => getDemandView(id),
      enabled: id > 0,
    });

  const queryClient = useQueryClient();

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandView,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: demandViewsQueryKeys.all() }),
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: updateDemandView,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          queryKey: demandViewsQueryKeys.detail(data.data.id),
        });
        await queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all(), exact: false });
        await queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() });
        return;
      },
    });

  const useArchive = () =>
    useErrorHandlingMutation({
      mutationFn: archiveDemandView,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: demandViewsQueryKeys.all() }),
          queryClient.resetQueries({ queryKey: demandViewsQueryKeys.lists() }),
        ]),
    });

  const useRestore = () =>
    useErrorHandlingMutation({
      mutationFn: restoreDemandView,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: demandViewsQueryKeys.all() }),
          queryClient.resetQueries({ queryKey: demandViewsQueryKeys.lists() }),
        ]),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandView,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: demandViewsQueryKeys.all() }),
    });

  const useGetActivityLog = (id: number) =>
    useQuery({
      queryKey: demandViewsQueryKeys.activityLogs(id),
      queryFn: () => getDemandViewActivityLogs(id),
    });

  return {
    getDemandViews,
    useGetAll,
    useGetDemandView,
    useCreate,
    useUpdate,
    useArchive,
    useRestore,
    useDelete,
    useGetActivityLog,
  };
};
